/**
 * DetailsTab Component
 * Organization branding, details, address, and tax information
 */
import "./detailsAthletic.css";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from "../../components/ui/select";
import { 
  Building2, Upload, Trash2, Check, X, AlertCircle, Loader2
} from "lucide-react";

export const SPORTS_OPTIONS = [
  "Soccer", "Basketball", "Baseball", "Softball", "Football",
  "Volleyball", "Hockey", "Lacrosse", "Tennis", "Swimming",
  "Track & Field", "Gymnastics", "Wrestling", "Golf", 
  "Cheerleading", "Dance", "Multi-Sport", "Other"
];

const DetailsTab = ({ 
  orgDetails, 
  setOrgDetails,
  handleLogoUpload,
  handleSplashUpload,
  handleSidebarUpload,
  handleSaveDetails,
  saving,
  subdomainChecking,
  subdomainAvailable,
  subdomainError
}) => {
  return (
    <div className="sh-details space-y-6" data-testid="details-tab">
      {/* Branding Section */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Branding</h3>
        
        {/* Existing upload handlers and image values remain parent-owned. */}
        <div className="details-images">
          {[
            ['logo_url', 'Organization Logo', 'Square format, 200x200px', handleLogoUpload],
            ['splash_image_url', 'Splash Banner', 'Banner image for portal (1200x400px)', handleSplashUpload],
            ['sidebar_image_url', 'Sidebar Background', 'Portal sidebar (800x1200px)', handleSidebarUpload]
          ].map(([field, label, hint, upload]) => (
            <div key={field} className="details-image">
              <p className="details-image-title">{label}</p>
              <p className="details-image-hint">{hint}</p>
              <div className="details-image-preview">
                {orgDetails[field] ? <img src={orgDetails[field]} alt={label} /> : <Building2 aria-hidden="true" />}
              </div>
              <div className="details-image-actions">
                <button type="button" aria-label={`Upload ${label}`} onClick={() => document.getElementById(`details-upload-${field}`).click()}>
                  <Upload aria-hidden="true" /> Upload
                </button>
                <input id={`details-upload-${field}`} aria-label={`${label} file`} type="file" accept="image/*" hidden onChange={upload} />
                {orgDetails[field] && <button type="button" aria-label={`Remove ${label}`} onClick={() => setOrgDetails(prev => ({ ...prev, [field]: '' }))}>
                  <Trash2 aria-hidden="true" /> Remove
                </button>}
              </div>
            </div>
          ))}
        </div>

        {/* Brand Colors */}
        <div className="grid grid-cols-2 gap-6">
          <div>
            <Label htmlFor="details-primary_color" className="text-slate-900">Primary Color</Label>
            <p className="text-xs text-slate-500 mb-2">Used for sidebar gradient & accents</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                aria-label="Primary Color picker"
                id="details-primary_color-picker"
                value={orgDetails.primary_color}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, primary_color: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                className="border-slate-300 uppercase"
                id="details-primary_color"
                value={orgDetails.primary_color}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, primary_color: e.target.value }))}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="details-secondary_color" className="text-slate-900">Secondary Color</Label>
            <p className="text-xs text-slate-500 mb-2">Used for gradient blend</p>
            <div className="flex items-center gap-3">
              <input
                type="color"
                aria-label="Secondary Color picker"
                id="details-secondary_color-picker"
                value={orgDetails.secondary_color}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, secondary_color: e.target.value }))}
                className="w-10 h-10 rounded cursor-pointer"
              />
              <Input
                className="border-slate-300 uppercase"
                id="details-secondary_color"
                value={orgDetails.secondary_color}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, secondary_color: e.target.value }))}
              />
            </div>
          </div>
        </div>
        
        {/* Color Preview */}
        {(orgDetails.primary_color || orgDetails.secondary_color) && (
          <div className="mt-4 flex items-center gap-3">
            <div 
              className="w-full h-12 rounded-xl"
              style={{ 
                background: `linear-gradient(135deg, ${orgDetails.primary_color || '#6366F1'} 0%, ${orgDetails.secondary_color || '#F97316'} 100%)` 
              }}
            />
            <span className="text-xs text-slate-500 whitespace-nowrap">Gradient Preview</span>
          </div>
        )}
      </div>

      {/* Organization Details Section */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-slate-900 mb-6">Organization Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <Label htmlFor="details-name" className="text-slate-900">Organization Name *</Label>
            <Input
              className="mt-1 border-slate-300"
              id="details-name" aria-required="true"
                value={orgDetails.name}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, name: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="details-sport" className="text-slate-900">Primary Sport</Label>
            <Select
              value={orgDetails.sport || ""}
              onValueChange={(value) => setOrgDetails(prev => ({ ...prev, sport: value }))}
            >
              <SelectTrigger id="details-sport" className="mt-1 border-slate-300">
                <SelectValue placeholder="Select a sport..." />
              </SelectTrigger>
              <SelectContent>
                {SPORTS_OPTIONS.map(sport => (
                  <SelectItem key={sport} value={sport}>{sport}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="details-website" className="text-slate-900">Website</Label>
            <Input
              className="mt-1 border-slate-300"
              placeholder="https://example.com"
              id="details-website"
                value={orgDetails.website}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, website: e.target.value }))}
            />
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="details-subdomain" className="text-slate-900">Custom Subdomain</Label>
            <p className="text-sm text-slate-500 mb-2">
              Create a custom URL for your organization&apos;s public portal
            </p>
            <div className="flex items-center gap-2">
              <div className="relative flex-1">
                <Input
                  className={`mt-1 border-slate-300 pr-10 ${
                    subdomainError ? 'border-red-300 focus:border-red-500' : 
                    subdomainAvailable === true ? 'border-green-300 focus:border-green-500' : ''
                  }`}
                  placeholder="your-org-name"
                  id="details-subdomain"
                value={orgDetails.subdomain}
                  onChange={(e) => {
                    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
                    setOrgDetails(prev => ({ ...prev, subdomain: value }));
                  }}
                  data-testid="subdomain-input"
                />
                <div className="absolute right-3 top-1/2 -translate-y-1/2 mt-0.5">
                  {subdomainChecking && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  {!subdomainChecking && subdomainAvailable === true && <Check className="w-4 h-4 text-green-500" />}
                  {!subdomainChecking && subdomainAvailable === false && <X className="w-4 h-4 text-red-500" />}
                </div>
              </div>
              <span className="text-slate-500 text-sm whitespace-nowrap">.sporthouse.ai</span>
            </div>
            {subdomainError && (
              <p className="text-sm text-red-500 mt-1 flex items-center gap-1">
                <AlertCircle className="w-3 h-3" />
                {subdomainError}
              </p>
            )}
            {orgDetails.subdomain && subdomainAvailable && (
              <p className="text-sm text-green-600 mt-1 flex items-center gap-1">
                <Check className="w-3 h-3" />
                Preview: <a href={`/org/${orgDetails.subdomain}`} target="_blank" rel="noopener noreferrer" className="underline hover:no-underline">
                  {orgDetails.subdomain}.sporthouse.ai
                </a>
              </p>
            )}
          </div>
          <div className="md:col-span-2">
            <Label htmlFor="details-description" className="text-slate-900">Description</Label>
            <Textarea
              className="mt-1 border-slate-300"
              rows={3}
              placeholder="Tell parents about your organization..."
              id="details-description"
                value={orgDetails.description}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, description: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="details-contact_email" className="text-slate-900">Contact Email *</Label>
            <p className="text-xs text-slate-500 mb-1">Shown on your public registration page</p>
            <Input
              type="email"
              className="border-slate-300"
              id="details-contact_email" aria-required="true"
                value={orgDetails.contact_email}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, contact_email: e.target.value }))}
            />
          </div>
          <div>
            <Label htmlFor="details-contact_phone" className="text-slate-900">Contact Phone</Label>
            <Input
              className="mt-1 border-slate-300"
              placeholder="(555) 555-5555"
              id="details-contact_phone"
                value={orgDetails.contact_phone}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, contact_phone: e.target.value }))}
            />
          </div>
        </div>
      </div>

      {/* Address Section */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-slate-900">Address</h3>
          <div className="flex items-center gap-2">
            <Switch
              id="show_address"
              checked={orgDetails.show_address}
              onCheckedChange={(checked) => setOrgDetails(prev => ({ ...prev, show_address: checked }))}
            />
            <Label htmlFor="show_address" className="text-sm text-slate-600 cursor-pointer">
              Show on public page
            </Label>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="md:col-span-2">
            <Label htmlFor="details-address" className="text-slate-900">Street Address</Label>
            <Input
              className="mt-1 border-slate-300"
              id="details-address"
                value={orgDetails.address}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, address: e.target.value }))}
              placeholder="123 Main Street"
            />
          </div>
          <div>
            <Label htmlFor="details-city" className="text-slate-900">City</Label>
            <Input
              className="mt-1 border-slate-300"
              id="details-city"
                value={orgDetails.city}
              onChange={(e) => setOrgDetails(prev => ({ ...prev, city: e.target.value }))}
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="details-state" className="text-slate-900">State</Label>
              <Input
                className="mt-1 border-slate-300"
                id="details-state"
                value={orgDetails.state}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, state: e.target.value }))}
              />
            </div>
            <div>
              <Label htmlFor="details-zip_code" className="text-slate-900">ZIP Code</Label>
              <Input
                className="mt-1 border-slate-300"
                id="details-zip_code"
                value={orgDetails.zip_code}
                onChange={(e) => setOrgDetails(prev => ({ ...prev, zip_code: e.target.value }))}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Tax ID Section */}
      <div className="bg-white border border-slate-200/60 rounded-2xl p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">Tax Information</h3>
            <p className="text-sm text-slate-500 mt-1">For non-profit organizations</p>
          </div>
          <div className="flex items-center gap-2">
            <Switch
              id="show_tax_id"
              checked={orgDetails.show_tax_id}
              onCheckedChange={(checked) => setOrgDetails(prev => ({ ...prev, show_tax_id: checked }))}
            />
            <Label htmlFor="show_tax_id" className="text-sm text-slate-600 cursor-pointer">
              Show on public page
            </Label>
          </div>
        </div>
        <div>
          <Label htmlFor="details-tax_id" className="text-slate-900">Tax ID / EIN</Label>
          <p className="text-xs text-slate-500 mb-2">Your organization's Employer Identification Number (if applicable)</p>
          <Input
            className="mt-1 border-slate-300 max-w-xs"
            id="details-tax_id"
                value={orgDetails.tax_id}
            onChange={(e) => setOrgDetails(prev => ({ ...prev, tax_id: e.target.value }))}
            placeholder="XX-XXXXXXX"
          />
        </div>
      </div>

      <div className="flex justify-end">
        <Button
          onClick={handleSaveDetails}
          disabled={saving}
          className="details-save"
          data-testid="save-details-btn"
        >
          {saving ? "Saving..." : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

export default DetailsTab;
